import { useEffect, useState, useCallback, useRef } from "react";
import { DndContext, PointerSensor, TouchSensor, useSensor, useSensors, useDraggable, useDroppable } from "@dnd-kit/core";
import { CSS } from "@dnd-kit/utilities";
import { toast } from "sonner";
import {
  Loader2, GripVertical, ChevronLeft, ChevronRight, X, Copy, Mail, Trash2, Plus, Minus,
  MapPin, Phone, FileText, DollarSign, Send, ExternalLink, Upload, Star, Ban, Check, Pause, Play, Lock, Pencil,
} from "lucide-react";
import { Btn } from "@/components/Btn";
import { api } from "@/lib/api";
import { PhotoThumb } from "@/components/PhotoThumb";
import { TIERS, SURCHARGES, STAGES, STAGE_META } from "@/lib/pricing";
import { ScheduleSection } from "@/pages/admin/ScheduleSection";
import { RichText } from "@/components/RichText";

const uid = () => (crypto.randomUUID ? crypto.randomUUID() : String(Math.random()));
const detailsToHtml = (o) => o.description || (o.details?.length ? `<ul>${o.details.map((d) => `<li>${d}</li>`).join("")}</ul>` : "");

const money = (n) => `$${Number(n || 0).toLocaleString()}`;
const paidOf = (p) => (p.payments || []).reduce((s, x) => s + Number(x.amount || 0), 0);
const balText = (total, paid) => {
  const b = Number(total || 0) - paid;
  if (b > 0) return `Bal ${money(b)}`;
  if (b < 0) return `Overpaid ${money(-b)}`;
  return "Paid in full";
};


/* -------------------------------------------------- Card */
function ProjectCard({ p, onOpen, onStage }) {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({ id: p.id, data: { stage: p.stage } });
  const idx = STAGES.indexOf(p.stage);
  const paid = paidOf(p);
  const style = { transform: CSS.Translate.toString(transform), opacity: isDragging ? 0.4 : 1 };
  const stop = (e) => e.stopPropagation();
  return (
    <div ref={setNodeRef} style={style} data-testid={`board-card-${p.id}`}
      className="bg-white border-2 border-charcoal rounded-2xl p-3 shadow-brutsm select-none">
      <div className="flex items-start justify-between gap-2">
        <button {...attributes} {...listeners} aria-label="Drag" data-testid={`drag-${p.id}`}
          className="text-charcoal/40 hover:text-charcoal cursor-grab active:cursor-grabbing touch-none -ml-1"><GripVertical className="w-5 h-5" /></button>
        {p.total > 0 ? <span className="font-heading font-bold text-coral text-lg leading-none">{money(p.total)}</span>
          : <span className="text-[10px] font-body font-bold bg-charcoal/10 rounded-full px-2 py-0.5">LEAD</span>}
      </div>
      <div onClick={() => onOpen(p)} className="cursor-pointer mt-1" data-testid={`open-${p.id}`}>
        <div className="font-heading font-bold text-base leading-tight">{p.customer_name || "Unnamed"}</div>
        <div className="font-body text-xs text-charcoal/60 flex flex-wrap gap-x-2 mt-0.5">
          {p.customer_phone && <span><Phone className="w-3 h-3 inline" /> {p.customer_phone}</span>}
          {p.customer_city && <span><MapPin className="w-3 h-3 inline" /> {p.customer_city}</span>}
        </div>
        {p.preferred_contact && <span className="inline-block mt-1 text-[10px] font-body font-bold bg-sunny border border-charcoal rounded-full px-2 py-0.5">Prefers {p.preferred_contact}</span>}
        {paid > 0 && <div className="mt-1 text-xs font-body text-teal font-bold">Paid {money(paid)} · {balText(p.total, paid)}</div>}
        {(p.review_stopped || p.review_received) ? (
          <span className="inline-flex items-center gap-1 mt-1 text-[10px] font-body font-bold bg-red-500 text-cream rounded-full px-2 py-0.5"><Star className="w-3 h-3" /> Review finished</span>
        ) : p.review_paused ? (
          <span className="inline-flex items-center gap-1 mt-1 text-[10px] font-body font-bold bg-sunny text-charcoal rounded-full px-2 py-0.5"><Star className="w-3 h-3" /> Review paused · {p.review_count || 0} sent</span>
        ) : p.review_active ? (
          <span className="inline-flex items-center gap-1 mt-1 text-[10px] font-body font-bold bg-green-500 text-cream rounded-full px-2 py-0.5"><Star className="w-3 h-3" /> Review active · {p.review_count || 0} sent</span>
        ) : null}
      </div>
      <div className="flex items-center gap-1 mt-2" onPointerDown={stop}>
        <button disabled={idx <= 0} onClick={() => onStage(p, STAGES[idx - 1])} data-testid={`prev-${p.id}`}
          className="w-7 h-7 grid place-items-center rounded-lg border-2 border-charcoal bg-white disabled:opacity-30"><ChevronLeft className="w-4 h-4" /></button>
        <select value={p.stage} onChange={(e) => onStage(p, e.target.value)} data-testid={`stage-select-${p.id}`}
          className="flex-1 text-xs font-body font-bold bg-cream border-2 border-charcoal rounded-lg px-1 py-1 focus:outline-none">
          {STAGES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <button disabled={idx >= STAGES.length - 1} onClick={() => onStage(p, STAGES[idx + 1])} data-testid={`next-${p.id}`}
          className="w-7 h-7 grid place-items-center rounded-lg border-2 border-charcoal bg-white disabled:opacity-30"><ChevronRight className="w-4 h-4" /></button>
      </div>
    </div>
  );
}

/* -------------------------------------------------- Column */
function Column({ stage, items, onOpen, onStage }) {
  const { setNodeRef, isOver } = useDroppable({ id: stage });
  const meta = STAGE_META[stage] || {};
  return (
    <div className="min-w-[270px] w-[270px] shrink-0">
      <div className={`flex items-center gap-2 mb-2 px-1`}>
        <span className={`w-3 h-3 rounded-full ${meta.dot}`} />
        <h3 className="font-heading font-bold text-sm">{stage}</h3>
        <span className="text-xs font-body font-bold text-charcoal/50">{items.length}</span>
      </div>
      <div ref={setNodeRef} data-testid={`column-${stage}`}
        className={`space-y-3 rounded-2xl p-2 min-h-[120px] border-2 border-dashed transition-colors ${isOver ? "border-coral bg-coral/5" : "border-charcoal/15 bg-charcoal/[0.02]"}`}>
        {items.map((p) => <ProjectCard key={p.id} p={p} onOpen={onOpen} onStage={onStage} />)}
      </div>
    </div>
  );
}

/* -------------------------------------------------- Invoices */
function InvoiceSection({ project, reload }) {
  const [amount, setAmount] = useState("");
  const [desc, setDesc] = useState("");
  const [photos, setPhotos] = useState([]);
  const [busy, setBusy] = useState(false);
  const invoices = project.invoices || [];
  const pct = (n) => setAmount(String(Math.round((Number(project.total || 0) * n) / 100)));

  const uploadPhotos = async (files) => {
    const paths = [];
    for (const f of [...files].slice(0, 6)) {
      const fd = new FormData(); fd.append("file", f);
      try { const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } }); if (data?.path) paths.push(data.path); } catch { /* ignore */ }
    }
    return paths;
  };

  const create = async () => {
    if (!amount || Number(amount) <= 0) { toast.error("Enter an invoice amount"); return; }
    setBusy(true);
    try {
      const paths = photos.length ? await uploadPhotos(photos) : [];
      await api.post(`/quotes/${project.id}/invoices`, { amount: Number(amount), description: desc, photos: paths });
      toast.success("Invoice created!");
      setAmount(""); setDesc(""); setPhotos([]);
      reload();
    } catch { toast.error("Could not create invoice"); } finally { setBusy(false); }
  };
  const copyLink = (id) => { navigator.clipboard.writeText(`${window.location.origin}/invoice/${id}`); toast.success("Invoice link copied!"); };
  const sendInv = async (id) => { if (!window.confirm(`Email this invoice to ${project.customer_email || "the customer"}?`)) return; try { const { data } = await api.post(`/quotes/${project.id}/invoices/${id}/send`); toast.success(`Invoice emailed to ${data.sent_to}!`); reload(); } catch (e) { toast.error(e.response?.data?.detail || "Could not email"); } };
  const del = async (id) => { try { await api.delete(`/quotes/${project.id}/invoices/${id}`); reload(); } catch { toast.error("Could not delete"); } };

  const input = "w-full bg-white border-2 border-charcoal rounded-xl px-3.5 py-2.5 font-body text-base focus:outline-none focus:ring-2 focus:ring-sunny";
  return (
    <div className="bg-white border-2 border-charcoal rounded-2xl p-4 shadow-brut" data-testid="invoice-section">
      <h3 className="font-heading font-bold text-lg mb-3 flex items-center gap-2"><FileText className="w-5 h-5" /> Invoices</h3>
      <div className="space-y-2 mb-4">
        {invoices.length === 0 && <p className="font-body text-sm text-charcoal/50">No invoices yet.</p>}
        {invoices.map((inv) => (
          <div key={inv.id} className="border-2 border-charcoal/15 rounded-xl p-3" data-testid={`invoice-${inv.id}`}>
            <div className="flex items-center justify-between">
              <div><span className="font-heading font-bold text-coral">{money(inv.amount)}</span> <span className="font-body text-sm text-charcoal/70">{inv.description}</span></div>
              <span className={`text-[10px] font-body font-bold rounded-full px-2 py-0.5 ${inv.sent_at ? "bg-teal text-cream" : "bg-charcoal/10"}`}>{inv.sent_at ? "Sent" : "Draft"}</span>
            </div>
            {inv.photos?.length > 0 && <div className="flex gap-2 mt-2 flex-wrap">{inv.photos.map((p) => <PhotoThumb key={p} path={p} group={inv.photos} size="w-12 h-12" />)}</div>}
            <div className="flex flex-wrap gap-2 mt-2">
              <button onClick={() => copyLink(inv.id)} className="text-xs font-body font-bold bg-white border-2 border-charcoal rounded-full px-3 py-1" data-testid={`inv-copy-${inv.id}`}><Copy className="w-3 h-3 inline" /> Copy link</button>
              <a href={`/invoice/${inv.id}`} target="_blank" rel="noreferrer" className="text-xs font-body font-bold bg-white border-2 border-charcoal rounded-full px-3 py-1"><ExternalLink className="w-3 h-3 inline" /> Open</a>
              {project.customer_email && <button onClick={() => sendInv(inv.id)} className="text-xs font-body font-bold bg-sunny border-2 border-charcoal rounded-full px-3 py-1" data-testid={`inv-send-${inv.id}`}><Send className="w-3 h-3 inline" /> Email</button>}
              <button onClick={() => del(inv.id)} className="text-xs text-coral"><Trash2 className="w-3 h-3 inline" /></button>
            </div>
          </div>
        ))}
      </div>
      <div className="bg-cream border-2 border-charcoal rounded-xl p-3 space-y-2">
        <div className="flex flex-wrap gap-2">
          {[25, 50, 100].map((n) => <button key={n} onClick={() => pct(n)} disabled={!project.total} className="text-xs font-body font-bold bg-teal text-cream rounded-full px-3 py-1 border-2 border-charcoal disabled:opacity-40">{n}% of total</button>)}
        </div>
        <div className="flex items-center gap-2"><span>$</span><input type="number" className={input} placeholder="Amount" value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="invoice-amount" /></div>
        <input className={input} placeholder="Description (e.g. 50% deposit, final balance)" value={desc} onChange={(e) => setDesc(e.target.value)} data-testid="invoice-desc" />
        <label className="flex items-center gap-2 bg-white border-2 border-dashed border-charcoal rounded-xl px-3 py-2 cursor-pointer text-sm">
          <Upload className="w-4 h-4" /> <span className="font-body text-charcoal/70">{photos.length ? `${photos.length} photo(s)` : "Add photos (optional)"}</span>
          <input type="file" accept="image/*" multiple className="hidden" onChange={(e) => setPhotos([...e.target.files])} />
        </label>
        <Btn variant="primary" size="md" className="w-full" onClick={create} disabled={busy} data-testid="invoice-create">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Create invoice"}</Btn>
      </div>
    </div>
  );
}

/* -------------------------------------------------- Payments */
function PaymentSection({ project, reload }) {
  const [amount, setAmount] = useState("");
  const [method, setMethod] = useState("Cash");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const payments = project.payments || [];
  const paid = paidOf(project);
  const total = Number(project.total || 0);
  const balance = total - paid;

  const record = async () => {
    if (!amount || Number(amount) <= 0) { toast.error("Enter a payment amount"); return; }
    setBusy(true);
    try {
      await api.post(`/quotes/${project.id}/payments`, { amount: Number(amount), method, note });
      toast.success("Payment recorded!");
      setAmount(""); setNote("");
      reload();
    } catch { toast.error("Could not record payment"); } finally { setBusy(false); }
  };
  const del = async (id) => { try { await api.delete(`/quotes/${project.id}/payments/${id}`); reload(); } catch { toast.error("Could not delete"); } };

  const input = "w-full bg-white border-2 border-charcoal rounded-xl px-3.5 py-2.5 font-body text-base focus:outline-none focus:ring-2 focus:ring-sunny";
  return (
    <div className="bg-white border-2 border-charcoal rounded-2xl p-4 shadow-brut" data-testid="payment-section">
      <h3 className="font-heading font-bold text-lg mb-3 flex items-center gap-2"><DollarSign className="w-5 h-5" /> Payments</h3>
      <div className="grid grid-cols-3 gap-2 mb-3 text-center">
        <div className="bg-cream border-2 border-charcoal rounded-xl py-2"><div className="text-[10px] font-body text-charcoal/60">Total</div><div className="font-heading font-bold">{money(total)}</div></div>
        <div className="bg-teal text-cream border-2 border-charcoal rounded-xl py-2"><div className="text-[10px] font-body opacity-80">Paid</div><div className="font-heading font-bold" data-testid="pay-paid">{money(paid)}</div></div>
        <div className={`border-2 border-charcoal rounded-xl py-2 ${balance <= 0 && total > 0 ? "bg-green-500 text-cream" : "bg-coral text-cream"}`}><div className="text-[10px] font-body opacity-80">{balance < 0 ? "Overpaid" : "Balance"}</div><div className="font-heading font-bold" data-testid="pay-balance">{money(Math.abs(balance))}</div></div>
      </div>
      <div className="space-y-2 mb-3">
        {payments.map((pmt) => (
          <div key={pmt.id} className="flex items-center justify-between border-2 border-charcoal/15 rounded-xl px-3 py-2 text-sm" data-testid={`payment-${pmt.id}`}>
            <span><b className="text-teal">{money(pmt.amount)}</b> · {pmt.method}{pmt.note ? ` · ${pmt.note}` : ""}</span>
            <button onClick={() => del(pmt.id)} className="text-coral"><Trash2 className="w-3 h-3" /></button>
          </div>
        ))}
      </div>
      <div className="bg-cream border-2 border-charcoal rounded-xl p-3 space-y-2">
        <div className="flex gap-2">
          <div className="flex items-center gap-1 flex-1"><span>$</span><input type="number" className={input} placeholder="Amount" value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="payment-amount" /></div>
          <select className={`${input} w-32`} value={method} onChange={(e) => setMethod(e.target.value)} data-testid="payment-method">
            {["Cash", "Zelle", "Venmo", "Cash App", "Check", "Card", "Other"].map((m) => <option key={m}>{m}</option>)}
          </select>
        </div>
        <input className={input} placeholder="Note (optional)" value={note} onChange={(e) => setNote(e.target.value)} />
        <Btn variant="secondary" size="md" className="w-full" onClick={record} disabled={busy} data-testid="payment-record">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Record payment"}</Btn>
      </div>
    </div>
  );
}

/* -------------------------------------------------- Review drip */
function ReviewSection({ project, reload }) {
  const [busy, setBusy] = useState(false);
  const stopped = project.review_stopped || project.review_received;
  const paused = project.review_paused && !stopped;
  const active = project.review_active && !paused && !stopped;
  const count = project.review_count || 0;
  const act = async (path, msg) => { setBusy(true); try { await api.post(`/quotes/${project.id}/review/${path}`); toast.success(msg); reload(); } catch (e) { toast.error(e.response?.data?.detail || "Something went wrong"); } finally { setBusy(false); } };
  return (
    <div className="bg-white border-2 border-charcoal rounded-2xl p-4 shadow-brut" data-testid="review-section">
      <h3 className="font-heading font-bold text-lg mb-2 flex items-center gap-2"><Star className="w-5 h-5" /> Review requests</h3>
      <div className="mb-3 text-sm font-body">
        {stopped ? <span className="inline-flex items-center gap-2 bg-red-500 text-cream font-bold rounded-full px-3 py-1" data-testid="review-status"><Check className="w-4 h-4" /> Finished — {count} sent</span>
          : paused ? <span className="inline-flex items-center gap-2 bg-sunny text-charcoal font-bold rounded-full px-3 py-1" data-testid="review-status"><Pause className="w-4 h-4" /> Paused · {count} sent</span>
            : active ? <span className="inline-flex items-center gap-2 bg-green-500 text-cream font-bold rounded-full px-3 py-1" data-testid="review-status">Active · {count} sent · every 3 days</span>
              : <span className="text-charcoal/60" data-testid="review-status">Starts automatically when the balance is paid in full. You can also send one now.</span>}
      </div>
      <div className="flex flex-wrap gap-2">
        {!stopped && <button onClick={() => act("send-now", "Review request sent!")} disabled={busy || !project.customer_email} className="text-xs font-body font-bold bg-white border-2 border-charcoal rounded-full px-4 py-2 disabled:opacity-40" data-testid="review-send-now"><Send className="w-3 h-3 inline" /> Send now</button>}
        {active && <button onClick={() => act("pause", "Review requests paused.")} disabled={busy} className="text-xs font-body font-bold bg-sunny border-2 border-charcoal rounded-full px-4 py-2" data-testid="review-pause"><Pause className="w-3 h-3 inline" /> Pause</button>}
        {paused && <button onClick={() => act("resume", "Review requests resumed.")} disabled={busy} className="text-xs font-body font-bold bg-green-500 text-cream border-2 border-charcoal rounded-full px-4 py-2" data-testid="review-resume"><Play className="w-3 h-3 inline" /> Resume</button>}
        {!stopped && <button onClick={() => act("stop", "Review requests stopped.")} disabled={busy} className="text-xs font-body font-bold bg-red-500 text-cream border-2 border-charcoal rounded-full px-4 py-2" data-testid="review-stop"><Ban className="w-3 h-3 inline" /> Stop (finished)</button>}
        {stopped && <span className="text-xs font-body text-charcoal/50 py-2">Requests are off — they won't get any more review emails.</span>}
      </div>
      {!project.customer_email && <p className="mt-2 text-xs font-body text-coral">No customer email on file — add one above to send review requests.</p>}
    </div>
  );
}

/* -------------------------------------------------- Modal */
/* -------------------------------------------------- Job photos */
function PhotosSection({ project, reload }) {
  const [label, setLabel] = useState("before");
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);
  const photos = project.job_photos || [];
  const customer = project.photo_paths || [];
  const GROUPS = [["before", "Before"], ["after", "After"], ["other", "Other"]];

  const upload = async (files) => {
    if (!files || !files.length) return;
    setBusy(true);
    let ok = 0;
    for (const file of Array.from(files).slice(0, 20)) {
      try {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        if (data?.path) { await api.post(`/quotes/${project.id}/photos`, { path: data.path, label }); ok += 1; }
      } catch (e) { toast.error(e.response?.data?.detail || `Could not upload ${file.name}`); }
    }
    if (ok) toast.success(`${ok} photo${ok > 1 ? "s" : ""} added`);
    if (fileRef.current) fileRef.current.value = "";
    setBusy(false);
    reload();
  };
  const remove = async (path) => {
    if (!window.confirm("Remove this photo from the project?")) return;
    try { await api.delete(`/quotes/${project.id}/photos`, { params: { path } }); reload(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not remove the photo"); }
  };

  return (
    <div className="space-y-5" data-testid="photos-section">
      <div className="bg-white border-2 border-charcoal rounded-2xl p-5 shadow-brut">
        <h3 className="font-heading font-bold text-lg mb-3">Add job photos</h3>
        <div className="flex gap-2 flex-wrap mb-4">
          {GROUPS.map(([id, l]) => (
            <button key={id} type="button" onClick={() => setLabel(id)} data-testid={`photos-label-${id}`}
              className={`font-heading font-bold text-sm rounded-full px-4 py-2 border-2 border-charcoal transition-colors ${label === id ? "bg-coral text-cream" : "bg-white"}`}>{l}</button>
          ))}
        </div>
        <label className={`inline-flex items-center gap-2 font-heading font-bold rounded-full px-5 py-2.5 border-2 border-charcoal bg-sunny ${busy ? "opacity-60" : "cursor-pointer"}`} data-testid="photos-add">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
          {busy ? "Uploading..." : `Add ${label} photos`}
          <input ref={fileRef} type="file" accept="image/*" multiple className="hidden" disabled={busy} onChange={(e) => upload(e.target.files)} data-testid="photos-input" />
        </label>
      </div>
      {GROUPS.map(([id, l]) => {
        const list = photos.filter((p) => (p.label || "other") === id);
        if (!list.length) return null;
        return (
          <div key={id} data-testid={`photos-group-${id}`}>
            <h4 className="font-heading font-bold mb-2">{l} ({list.length})</h4>
            <div className="flex gap-3 flex-wrap">
              {list.map((p) => (
                <div key={p.path} className="relative">
                  <PhotoThumb path={p.path} group={list.map((x) => x.path)} size="w-24 h-24" />
                  <button type="button" onClick={() => remove(p.path)} aria-label="Remove photo" data-testid="photos-remove"
                    className="absolute -top-2 -right-2 bg-coral text-cream rounded-full p-1 border-2 border-charcoal"><X className="w-3 h-3" /></button>
                </div>
              ))}
            </div>
          </div>
        );
      })}
      {customer.length > 0 && (
        <div>
          <h4 className="font-heading font-bold mb-2">From the customer ({customer.length})</h4>
          <div className="flex gap-3 flex-wrap">{customer.map((p) => <PhotoThumb key={p} path={p} group={customer} size="w-24 h-24" />)}</div>
        </div>
      )}
      {!photos.length && !customer.length && <p className="font-body text-sm text-charcoal/60">No photos on this project yet.</p>}
    </div>
  );
}

function ProjectModal({ project, onClose, reload }) {
  const scrollRef = useRef(null);
  const [c, setC] = useState(project);
  const [items, setItems] = useState(project.line_items || []);
  const [useOptions, setUseOptions] = useState((project.options || []).length > 0);
  const [selectionMode, setSelectionMode] = useState(project.selection_mode || "multi");
  const [options, setOptions] = useState((project.options || []).map((o) => ({ ...o, description: detailsToHtml(o) })));
  const [busy, setBusy] = useState(false);
  const [mtab, setMtab] = useState("details");
  const [editMode, setEditMode] = useState(false);
  const [cashEnabled, setCashEnabled] = useState(!!project.cash_enabled);
  const [cashPrice, setCashPrice] = useState(project.cash_price || "");
  useEffect(() => {
    setC(project);
    setItems(project.line_items || []);
    setUseOptions((project.options || []).length > 0);
    setSelectionMode(project.selection_mode || "multi");
    setOptions((project.options || []).map((o) => ({ ...o, description: detailsToHtml(o) })));
    setCashEnabled(!!project.cash_enabled);
    setCashPrice(project.cash_price || "");
    setEditMode(false);
  }, [project]);
  useEffect(() => { scrollRef.current?.scrollTo(0, 0); }, []);
  const set = (k) => (e) => setC((s) => ({ ...s, [k]: e.target.value }));
  const total = items.reduce((s, i) => s + Number(i.amount || 0), 0);
  const setTier = (t) => setItems((prev) => [{ label: t.label, amount: t.amount }, ...prev.filter((i) => !TIERS.some((x) => x.label === i.label))]);
  const addItem = (it) => setItems((prev) => [...prev, { ...it }]);
  const addOpt = (addon = false) => setOptions((prev) => [...prev, { id: uid(), title: "", price: "", description: "", addon, cash_price: "" }]);
  const setOptField = (id, k, v) => setOptions((prev) => prev.map((o) => o.id === id ? { ...o, [k]: v } : o));
  const rmOpt = (id) => setOptions((prev) => prev.filter((o) => o.id !== id));

  const save = async () => {
    setBusy(true);
    try {
      const cleanOpts = useOptions
        ? options.filter((o) => (o.title || "").trim()).map((o) => ({ id: o.id, title: o.title.trim(), price: Number(o.price) || 0, description: o.description || "", addon: !!o.addon, cash_price: Number(o.cash_price) || 0 }))
        : [];
      await api.put(`/quotes/${project.id}`, {
        customer_name: c.customer_name || "", customer_phone: c.customer_phone || "", customer_email: c.customer_email || "",
        customer_address: c.customer_address || "", customer_city: c.customer_city || "", job_description: c.job_description || "",
        load_size: c.load_size || "", lead_id: project.lead_id || null,
        line_items: useOptions ? (project.line_items || []) : items,
        total: useOptions ? Number(project.total || 0) : total,
        notes: c.notes || "", options: cleanOpts, selection_mode: selectionMode,
        cash_enabled: cashEnabled, cash_price: Number(cashPrice) || 0,
      });
      toast.success("Saved!");
      setEditMode(false);
      reload();
    } catch { toast.error("Could not save"); } finally { setBusy(false); }
  };
  const copyQuote = () => { navigator.clipboard.writeText(`${window.location.origin}/quote/${project.id}`); toast.success("Quote link copied!"); };
  const emailQuote = async () => { if (!window.confirm(`Email this quote to ${c.customer_email}?`)) return; try { const { data } = await api.post(`/quotes/${project.id}/email`); toast.success(`Quote emailed to ${data.sent_to}!`); reload(); } catch (e) { toast.error(e.response?.data?.detail || "Could not email"); } };
  const deleteProject = async () => { if (!window.confirm("Delete this project permanently?")) return; try { await api.delete(`/quotes/${project.id}`); toast.success("Project deleted"); onClose(); reload(); } catch { toast.error("Could not delete"); } };

  const input = "w-full bg-white border-2 border-charcoal rounded-xl px-3.5 py-2.5 font-body text-base focus:outline-none focus:ring-2 focus:ring-sunny";
  const signed = project.status === "signed";
  const locked = signed && !editMode;
  const chosen = (project.options || []).filter((o) => (project.selected_option_ids || []).includes(o.id));
  const lockedCash = project.cash_enabled
    ? (chosen.length > 0
        ? chosen.reduce((s, o) => s + (Number(o.cash_price) > 0 ? Number(o.cash_price) : Number(o.price) || 0), 0)
        : Number(project.cash_price) || 0)
    : 0;
  const signedDate = project.signed_at ? new Date(project.signed_at).toLocaleDateString(undefined, { month: "long", day: "numeric", year: "numeric" }) : "";

  const lockedView = (
    <div className="space-y-4" data-testid="m-locked-view">
      <div className="bg-teal/10 border-2 border-teal rounded-2xl p-4 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2 font-heading font-bold text-teal"><Lock className="w-5 h-5" /> Signed & locked{project.signer_name ? ` — ${project.signer_name}` : ""}{signedDate ? ` · ${signedDate}` : ""}</div>
        <button onClick={() => { if (window.confirm("This quote was already signed by the customer. Edit it on your side anyway?")) setEditMode(true); }} data-testid="m-edit-toggle"
          className="inline-flex items-center gap-1 font-heading font-bold text-xs bg-white border-2 border-charcoal rounded-full px-4 py-2 hover:bg-sunny transition-colors"><Pencil className="w-3.5 h-3.5" /> Edit quote</button>
      </div>
      <div className="bg-white border-2 border-charcoal rounded-2xl p-4 shadow-brut">
        <h3 className="font-heading font-bold text-lg mb-2">Customer</h3>
        <div className="font-body text-sm space-y-0.5">
          <div className="font-bold">{c.customer_name}</div>
          {c.customer_phone && <div>{c.customer_phone}</div>}
          {c.customer_email && <div>{c.customer_email}</div>}
          {(c.customer_address || c.customer_city) && <div className="text-charcoal/70">{[c.customer_address, c.customer_city].filter(Boolean).join(", ")}</div>}
        </div>
        {c.job_description && <div className="mt-3 bg-cream border-2 border-charcoal rounded-xl p-3"><div className="font-heading font-bold text-xs mb-1">Job description</div><div className="rich-content font-body text-sm text-charcoal/80" dangerouslySetInnerHTML={{ __html: c.job_description }} /></div>}
        {project.photo_paths?.length > 0 && <div className="flex gap-2 mt-3 flex-wrap">{project.photo_paths.map((p) => <PhotoThumb key={p} path={p} group={project.photo_paths} />)}</div>}
      </div>
      <div className="bg-white border-2 border-charcoal rounded-2xl p-4 shadow-brut">
        <h3 className="font-heading font-bold text-lg mb-2">{chosen.length > 0 ? "What the customer chose" : "Pricing"}</h3>
        {chosen.length > 0 ? (
          <div className="space-y-3">
            {chosen.map((o) => (
              <div key={o.id} className="border-2 border-charcoal rounded-xl p-3 bg-teal/5">
                <div className="flex items-baseline justify-between gap-2"><span className="font-heading font-bold">{o.title}{o.addon ? " (add-on)" : ""}</span><span className="font-heading font-bold text-coral">{money(o.price)}</span></div>
                {o.description && <div className="rich-content mt-1.5 font-body text-sm text-charcoal/80" dangerouslySetInnerHTML={{ __html: o.description }} />}
                {(!o.description && o.details?.length > 0) && <ul className="mt-1.5 space-y-1">{o.details.map((d, i) => <li key={i} className="font-body text-sm text-charcoal/80 flex gap-2"><span className="text-teal">•</span><span>{d}</span></li>)}</ul>}
              </div>
            ))}
          </div>
        ) : (
          <div className="space-y-1">
            {(project.line_items || []).map((li, i) => <div key={i} className="flex items-center justify-between font-body text-sm py-1 border-b border-charcoal/10"><span>{li.label}</span><span className="font-semibold">{money(li.amount)}</span></div>)}
          </div>
        )}
        <div className="flex items-center justify-between border-t-2 border-charcoal pt-2 mt-3"><span className="font-heading font-bold text-xl">Total</span><span className="font-heading font-bold text-xl text-coral">{money(project.total)}</span></div>
        {project.cash_enabled && lockedCash > 0 && (
          <div className="mt-2 flex items-center justify-between bg-green-50 border-2 border-green-600 rounded-xl px-3 py-2" data-testid="m-locked-cash">
            <span className="font-heading font-bold text-green-800 text-sm">Cash price {project.total > lockedCash && <span className="font-body text-xs">(save {money(project.total - lockedCash)})</span>}</span>
            <span className="font-heading font-bold text-green-800">{money(lockedCash)}</span>
          </div>
        )}
        {project.customer_message && <div className="mt-3 bg-sunny/30 border-2 border-charcoal rounded-xl p-3"><div className="font-heading font-bold text-xs mb-1">Customer's note</div><p className="font-body text-sm text-charcoal/80 italic">"{project.customer_message}"</p></div>}
        <div className="flex flex-wrap gap-2 mt-3">
          <button onClick={copyQuote} className="text-xs font-body font-bold bg-white border-2 border-charcoal rounded-full px-3 py-1"><Copy className="w-3 h-3 inline" /> Quote link</button>
          {c.customer_email && <button onClick={emailQuote} className="text-xs font-body font-bold bg-sunny border-2 border-charcoal rounded-full px-3 py-1"><Mail className="w-3 h-3 inline" /> Email quote</button>}
        </div>
      </div>
    </div>
  );
  return (
    <div ref={scrollRef} className="fixed inset-0 z-[60] bg-charcoal/60 flex items-start justify-center p-2 md:p-6 overflow-y-auto" onClick={onClose} data-testid="project-modal">
      <div className="bg-cream border-2 border-charcoal rounded-2xl w-full max-w-4xl my-2 md:my-4 shadow-brutlg" onClick={(e) => e.stopPropagation()}>
        <div className="sticky top-0 bg-charcoal text-cream rounded-t-2xl px-5 py-3 flex items-center justify-between z-10">
          <div className="font-heading font-bold text-lg">{c.customer_name || "Project"} <span className="text-coral">{money(useOptions ? Number(project.total || 0) : total)}</span></div>
          <div className="flex items-center gap-3">
            
            <button onClick={onClose} data-testid="modal-close" aria-label="Close" className="w-11 h-11 -mr-2 inline-flex items-center justify-center rounded-full hover:bg-cream/10"><X className="w-7 h-7" /></button>
          </div>
        </div>
        <div className="p-4 md:p-5 space-y-4">
          <div className="flex gap-2 flex-wrap">
            {[["details", "Details & Pricing"], ["schedule", "Schedule"], ["photos", "Photos"], ["invoices", "Invoices"], ["payments", "Payments"]].map(([id, label]) => {
              const count = id === "invoices" ? (project.invoices || []).length : id === "payments" ? (project.payments || []).length : id === "schedule" ? (project.events || []).length : id === "photos" ? (project.job_photos || []).length + (project.photo_paths || []).length : 0;
              return (
                <button key={id} onClick={() => setMtab(id)} data-testid={`mtab-${id}`}
                  className={`font-heading font-bold text-sm rounded-full px-4 py-2 border-2 border-charcoal transition-colors ${mtab === id ? "bg-coral text-cream" : "bg-white"}`}>
                  {label}{count > 0 ? ` (${count})` : ""}
                </button>
              );
            })}
          </div>

          {mtab === "details" && (locked ? lockedView : (
          <div className="grid md:grid-cols-2 gap-4">
            {signed && editMode && (
              <div className="md:col-span-2 bg-sunny/40 border-2 border-charcoal rounded-xl p-3 flex flex-wrap items-center justify-between gap-2">
                <span className="font-body text-sm font-bold">You're editing a quote the customer already signed. Save when done.</span>
                <button onClick={() => setEditMode(false)} data-testid="m-lock-toggle" className="inline-flex items-center gap-1 font-heading font-bold text-xs bg-white border-2 border-charcoal rounded-full px-3 py-1.5"><Lock className="w-3.5 h-3.5" /> Lock again</button>
              </div>
            )}
            {/* customer + pricing */}
            <div className="space-y-4">
              <div className="bg-white border-2 border-charcoal rounded-2xl p-4 shadow-brut">
                <h3 className="font-heading font-bold text-lg mb-3">Customer</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  <input className={input} placeholder="Name" value={c.customer_name || ""} onChange={set("customer_name")} data-testid="m-name" />
                  <input className={input} placeholder="Phone" value={c.customer_phone || ""} onChange={set("customer_phone")} />
                  <input className={`${input} sm:col-span-2`} placeholder="Email" value={c.customer_email || ""} onChange={set("customer_email")} data-testid="m-email" />
                  <input className={input} placeholder="City" value={c.customer_city || ""} onChange={set("customer_city")} />
                  <input className={input} placeholder="Address" value={c.customer_address || ""} onChange={set("customer_address")} />
                  <div className="sm:col-span-2">
                    <label className="font-body text-xs font-bold text-charcoal/70 mb-1 block">Job description</label>
                    <RichText value={c.job_description || ""} onChange={(html) => setC((s) => ({ ...s, job_description: html }))} placeholder="Describe the job — bullets, bold, alignment…" testid="m-jobdesc" />
                  </div>
                  <textarea className={`${input} sm:col-span-2`} placeholder="Private notes" value={c.notes || ""} onChange={set("notes")} />
                </div>
                {project.photo_paths?.length > 0 && <div className="flex gap-2 mt-3 flex-wrap">{project.photo_paths.map((p) => <PhotoThumb key={p} path={p} group={project.photo_paths} />)}</div>}
              </div>
              <div className="bg-white border-2 border-charcoal rounded-2xl p-4 shadow-brut">
                <h3 className="font-heading font-bold text-lg mb-2">Pricing</h3>
                <div className="flex flex-wrap gap-2 mb-3">
                  <button onClick={() => setUseOptions(false)} data-testid="m-pricing-fixed"
                    className={`font-heading font-bold text-xs rounded-full px-4 py-1.5 border-2 border-charcoal transition-colors ${!useOptions ? "bg-teal text-cream" : "bg-white"}`}>Fixed price</button>
                  <button onClick={() => setUseOptions(true)} data-testid="m-pricing-options"
                    className={`font-heading font-bold text-xs rounded-full px-4 py-1.5 border-2 border-charcoal transition-colors ${useOptions ? "bg-teal text-cream" : "bg-white"}`}>Give customer options</button>
                </div>
                {useOptions ? (
                  <div data-testid="m-options-builder">
                    {project.status === "signed" && (project.selected_option_ids || []).length > 0 && (
                      <div className="mb-3 bg-teal/10 border-2 border-teal rounded-xl p-3 text-sm font-body" data-testid="m-options-chosen">
                        Customer chose: <b>{project.options.filter((o) => (project.selected_option_ids || []).includes(o.id)).map((o) => o.title).join(", ")}</b> · <span className="text-coral font-bold">{money(project.total)}</span>
                      </div>
                    )}
                    <div className="flex flex-wrap items-center gap-2 mb-3">
                      <span className="font-body font-bold text-sm text-charcoal/70">Customer can</span>
                      <button onClick={() => setSelectionMode("multi")} data-testid="m-sel-multi"
                        className={`font-heading font-bold text-xs rounded-full px-3 py-1 border-2 border-charcoal transition-colors ${selectionMode === "multi" ? "bg-coral text-cream" : "bg-white"}`}>pick as many</button>
                      <button onClick={() => setSelectionMode("single")} data-testid="m-sel-single"
                        className={`font-heading font-bold text-xs rounded-full px-3 py-1 border-2 border-charcoal transition-colors ${selectionMode === "single" ? "bg-coral text-cream" : "bg-white"}`}>pick one (+ add-ons)</button>
                    </div>
                    <label className="flex items-center gap-2 mb-3 font-heading font-bold text-sm cursor-pointer bg-green-50 border-2 border-charcoal rounded-xl px-3 py-2">
                      <input type="checkbox" checked={cashEnabled} onChange={(e) => setCashEnabled(e.target.checked)} className="w-4 h-4 accent-teal" data-testid="m-opt-cash-toggle" /> Offer a cash price per option
                    </label>
                    <div className="space-y-3">
                      {options.map((o, idx) => (
                        <div key={o.id} className={`border-2 border-charcoal rounded-xl p-3 ${o.addon ? "bg-sunny/30" : "bg-cream"}`} data-testid={`m-option-${idx}`}>
                          <input className={`${input} w-full mb-2`} placeholder="Option title" value={o.title || ""} onChange={(e) => setOptField(o.id, "title", e.target.value)} data-testid={`m-opt-title-${idx}`} />
                          <div className="flex items-center gap-2 mb-2 flex-wrap">
                            <div className="flex items-center gap-1"><span className="font-body font-bold">$</span><input type="number" className={`${input} w-24`} placeholder="Price" value={o.price} onChange={(e) => setOptField(o.id, "price", e.target.value)} data-testid={`m-opt-price-${idx}`} /></div>
                            {cashEnabled && <div className="flex items-center gap-1"><span className="font-body font-bold text-green-700 text-sm">Cash $</span><input type="number" className={`${input} w-20`} placeholder="Cash" value={o.cash_price || ""} onChange={(e) => setOptField(o.id, "cash_price", e.target.value)} data-testid={`m-opt-cash-${idx}`} /></div>}
                            <button onClick={() => rmOpt(o.id)} className="ml-auto inline-flex items-center gap-1 text-coral font-body font-bold text-sm" data-testid={`m-opt-remove-${idx}`}><Trash2 className="w-4 h-4" /> Remove</button>
                          </div>
                          <RichText value={o.description} onChange={(html) => setOptField(o.id, "description", html)} placeholder="What's included — bullets, bold, alignment…" testid={`m-opt-desc-${idx}`} />
                          <label className="flex items-center gap-2 mt-2 font-body text-sm cursor-pointer">
                            <input type="checkbox" checked={!!o.addon} onChange={(e) => setOptField(o.id, "addon", e.target.checked)} className="w-4 h-4 accent-coral" data-testid={`m-opt-addon-${idx}`} /> Optional add-on
                          </label>
                        </div>
                      ))}
                    </div>
                    <div className="flex flex-wrap gap-2 mt-3">
                      <button onClick={() => addOpt(false)} className="text-xs font-heading font-bold bg-teal text-cream rounded-full px-4 py-1.5 border-2 border-charcoal" data-testid="m-add-option"><Plus className="w-3 h-3 inline" /> Add option</button>
                      <button onClick={() => addOpt(true)} className="text-xs font-heading font-bold bg-sunny rounded-full px-4 py-1.5 border-2 border-charcoal" data-testid="m-add-addon"><Plus className="w-3 h-3 inline" /> Add add-on</button>
                    </div>
                    <p className="font-body text-[11px] text-charcoal/50 mt-2">The total is set when the customer picks & signs. Email them the quote link below.</p>
                  </div>
                ) : (
                <>
                <p className="font-body text-xs text-charcoal/50 mb-2">$200 minimum on every job.</p>
                <div className="flex flex-wrap gap-2 mb-2">{TIERS.map((t) => <button key={t.label} onClick={() => setTier(t)} className="text-xs font-body font-bold bg-teal text-cream rounded-full px-3 py-1 border-2 border-charcoal">{t.label} ${t.amount}</button>)}</div>
                <div className="flex flex-wrap gap-2 mb-3">
                  {SURCHARGES.map((s) => <button key={s.label} onClick={() => addItem(s)} className="text-xs font-body font-bold bg-sunny rounded-full px-3 py-1 border-2 border-charcoal">+ {s.label}</button>)}
                  <button onClick={() => addItem({ label: "Custom item", amount: 0 })} className="text-xs font-body font-bold bg-white rounded-full px-3 py-1 border-2 border-charcoal"><Plus className="w-3 h-3 inline" /> Custom</button>
                  <button onClick={() => addItem({ label: "Discount", amount: 0 })} className="text-xs font-body font-bold bg-green-100 rounded-full px-3 py-1 border-2 border-charcoal hover:bg-green-200 transition-colors" data-testid="m-add-discount"><Minus className="w-3 h-3 inline" /> Discount</button>
                </div>
                <div className="space-y-2">
                  {items.map((it, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <input className={`${input} flex-1`} value={it.label} onChange={(e) => setItems((prev) => prev.map((x, idx) => idx === i ? { ...x, label: e.target.value } : x))} />
                      <div className="flex items-center gap-1"><span>$</span><input type="number" className={`${input} w-20`} value={it.amount} onChange={(e) => setItems((prev) => prev.map((x, idx) => idx === i ? { ...x, amount: e.target.value } : x))} /></div>
                      <button onClick={() => setItems((prev) => prev.filter((_, idx) => idx !== i))} className="text-coral"><Trash2 className="w-4 h-4" /></button>
                    </div>
                  ))}
                </div>
                <div className="mt-3 flex items-center justify-between border-t-2 border-charcoal pt-2">
                  <span className="font-heading font-bold text-xl">Total</span>
                  <span className="font-heading font-bold text-xl text-coral" data-testid="m-total">{money(total)}</span>
                </div>
                <p className="font-body text-[11px] text-charcoal/50 mt-1">For a discount, add a Discount line and enter a negative amount (e.g. -50).</p>
                <div className="mt-3 border-2 border-charcoal rounded-xl p-3 bg-green-50" data-testid="m-cash">
                  <label className="flex items-center gap-2 font-heading font-bold text-sm cursor-pointer">
                    <input type="checkbox" checked={cashEnabled} onChange={(e) => setCashEnabled(e.target.checked)} className="w-4 h-4 accent-teal" data-testid="m-cash-toggle" /> Offer a cash price
                  </label>
                  {cashEnabled && (
                    <div className="mt-2 flex items-center gap-2 flex-wrap">
                      <span className="font-body text-sm text-charcoal/70">Cash price</span>
                      <div className="flex items-center gap-1"><span>$</span><input type="number" className={`${input} w-28`} placeholder="e.g. 4700" value={cashPrice} onChange={(e) => setCashPrice(e.target.value)} data-testid="m-cash-price" /></div>
                      {Number(cashPrice) > 0 && total > 0 && <span className="text-xs font-body font-bold text-green-700 ml-auto">Save {money(Math.max(0, total - Number(cashPrice)))}</span>}
                    </div>
                  )}
                </div>
                </>
                )}
                <Btn variant="primary" className="w-full mt-3" onClick={save} disabled={busy} data-testid="m-save">{busy ? <Loader2 className="w-5 h-5 animate-spin" /> : "Save project"}</Btn>
                <div className="flex flex-wrap gap-2 mt-2">
                  <button onClick={copyQuote} className="text-xs font-body font-bold bg-white border-2 border-charcoal rounded-full px-3 py-1"><Copy className="w-3 h-3 inline" /> Quote link</button>
                  {c.customer_email && <button onClick={emailQuote} className="text-xs font-body font-bold bg-sunny border-2 border-charcoal rounded-full px-3 py-1"><Mail className="w-3 h-3 inline" /> Email quote</button>}
                  {project.status === "signed" && <span className="text-xs font-body font-bold bg-green-500 text-cream rounded-full px-3 py-1">Approved{project.signer_name ? ` by ${project.signer_name}` : ""}</span>}
                </div>
              </div>
            </div>
          </div>
          ))}
          {mtab === "schedule" && <div className="max-w-xl"><ScheduleSection project={project} reload={reload} /></div>}
          {mtab === "photos" && <div className="max-w-2xl"><PhotosSection project={project} reload={reload} /></div>}
          {mtab === "invoices" && <div className="max-w-xl"><InvoiceSection project={project} reload={reload} /></div>}
          {mtab === "payments" && (
            <div className="max-w-xl space-y-4">
              <PaymentSection project={project} reload={reload} />
              <ReviewSection project={project} reload={reload} />
            </div>
          )}
          <div className="pt-4 mt-2 border-t-2 border-charcoal/10 flex justify-end">
            <button onClick={deleteProject} data-testid="modal-delete" className="inline-flex items-center gap-2 font-body text-sm text-coral px-3 py-2 rounded-full hover:bg-coral/10"><Trash2 className="w-4 h-4" /> Delete project</button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* -------------------------------------------------- Board */
export default function ProjectBoard({ openLeadId, onOpened }) {
  const [projects, setProjects] = useState(null);
  const [openId, setOpenId] = useState(null);
  const [mStage, setMStage] = useState(null);
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 200, tolerance: 8 } }),
  );

  const load = useCallback(() => api.get("/quotes").then(({ data }) => setProjects(data)).catch(() => setProjects([])), []);
  useEffect(() => { load(); }, [load]);

  // When arriving from the Leads tab, auto-open that lead's project
  useEffect(() => {
    if (!openLeadId || !projects) return;
    const match = projects.find((p) => p.lead_id === openLeadId);
    if (match) setOpenId(match.id);
    else toast.error("No project found for this lead yet");
    onOpened && onOpened();
  }, [openLeadId, projects, onOpened]);

  // Make the browser Back button close the open project (instead of leaving the board)
  useEffect(() => {
    if (!openId) return;
    window.history.pushState({ skdModal: true }, "");
    const onPop = () => setOpenId(null);
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, [openId]);

  const closeModal = () => {
    if (window.history.state && window.history.state.skdModal) window.history.back();
    else setOpenId(null);
  };

  const updateStage = async (p, stage) => {
    if (!stage || stage === p.stage) return;
    setProjects((prev) => prev.map((x) => x.id === p.id ? { ...x, stage } : x));
    try { await api.patch(`/quotes/${p.id}/stage`, { stage }); } catch { toast.error("Could not move card"); load(); }
  };
  const onDragEnd = ({ active, over }) => {
    if (!over) return;
    const p = projects.find((x) => x.id === active.id);
    if (p && STAGES.includes(over.id)) updateStage(p, over.id);
  };

  if (!projects) return <div className="py-10 flex justify-center"><Loader2 className="w-7 h-7 animate-spin" /></div>;
  const openProject = projects.find((p) => p.id === openId) || null;
  const counts = Object.fromEntries(STAGES.map((s) => [s, projects.filter((p) => p.stage === s).length]));
  const activeStage = mStage || STAGES.find((s) => counts[s] > 0) || STAGES[0];

  return (
    <div data-testid="project-board">
      <p className="font-body text-sm text-charcoal/60 mb-3">Drag cards, use the arrows, or pick a stage. Tap a card to build the quote, send invoices and record payments.</p>
      <div className="md:hidden" data-testid="board-mobile">
        <div className="flex flex-wrap gap-2 mb-4">
          {STAGES.map((s) => (
            <button key={s} type="button" onClick={() => setMStage(s)} data-testid={`mstage-${s}`}
              className={`font-heading font-bold text-sm rounded-full px-3 py-2 border-2 border-charcoal transition-colors ${activeStage === s ? "bg-coral text-cream" : counts[s] ? "bg-white" : "bg-white opacity-50"}`}>
              {s} ({counts[s]})
            </button>
          ))}
        </div>
        <DndContext sensors={sensors} onDragEnd={onDragEnd}>
          <div className="space-y-3">
            {projects.filter((p) => p.stage === activeStage).map((p) => <ProjectCard key={p.id} p={p} onOpen={(x) => setOpenId(x.id)} onStage={updateStage} />)}
            {!counts[activeStage] && <p className="font-body text-sm text-charcoal/60">Nothing in {activeStage} right now.</p>}
          </div>
        </DndContext>
      </div>
      <div className="hidden md:block">
      <DndContext sensors={sensors} onDragEnd={onDragEnd}>
        <div className="flex gap-4 overflow-x-auto pb-4 -mx-2 px-2">
          {STAGES.map((stage) => (
            <Column key={stage} stage={stage} items={projects.filter((p) => p.stage === stage)} onOpen={(p) => setOpenId(p.id)} onStage={updateStage} />
          ))}
        </div>
      </DndContext>
      </div>
      {openProject && <ProjectModal project={openProject} onClose={closeModal} reload={load} />}
    </div>
  );
}
